// @ts-strict-ignore
import React, {useEffect, useRef, useState} from 'react';

import {getProjectId} from 'common/report/util';
import {SelectReact} from 'common/components/select';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {appApiUrl} from 'common/report/queries/utils';
import {GenerateSignedReplayParamsResponse} from 'app_api/projects/replays/types';
import {apiClient} from 'common/util/api-client';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {Directions} from 'common/components/drop-menu/types';
import {useTranslation} from 'common/i18n/hooks';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {isTestEnv} from 'common/report/util/env';
import {SwitchReact} from 'common/components/switch';
import {useThemes} from 'common/contexts/react/themes-context';
import {TooltipReact} from 'common/components/tooltip';
import {Trans} from 'common/i18n';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {SkeletonListReact} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';

import {CopyToClipboardButtonReact} from '../copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from '../copy-to-clipboard/copy-to-clipboard-button/types';
import styles from './index.module.styl';
import {
  EmbedReplayPayload,
  ParentAppReplayMessage,
  RecordingEventMetadata,
  RecordingInactivePeriod,
  RecordingMetadata,
  RecordingPageViewEventMetadata,
  RecordingSpeed,
  WindowMessageRecipient,
} from './types';
import {formatRecordingProgressTimeBar, sendEmbedReplayMessage, useReplayWindowMessageListener} from './util';
import {RecordingPlayerTimeline} from './timeline';

type RecordingPlayerProps = {
  children?: React.ReactNode;
  copyButton?: React.ReactNode;
  currentRecordingEventSelectedTime: number;
  currentRecordingPaused: boolean;
  isFullscreen: boolean;
  isSkipInactivityEnabled: boolean;
  playlistCollapsed: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  showSkipInactiveSwitch: boolean;
  searchFilter: string;
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  hasReplayPageViewEvents: boolean;

  onFinishReplay: (replayId: string) => void;
  onStartReplay: (replayId: string) => void;
  onToggleFullscreen: () => void;
  onToggleSkipInactivity: (isSkipInactivityEnabled: boolean) => void;
  onToggleCurrentRecordingPaused: (isPaused: boolean) => void;
  updateCurrentReplayEvent: (eventTime: number) => void;
};

type SignedReplayParams = GenerateSignedReplayParamsResponse['results'];

enum ErrorStateType {
  NoReplayFound = `no_replay_found`,
  EmbedBlocked = `embed_blocked`,
}

function ErrorState(props: {type: ErrorStateType}) {
  const {t} = useTranslation();

  let title;
  let subtitle;

  if (props.type === ErrorStateType.EmbedBlocked) {
    title = t(`session-replay.failed-to-load-player`);
    subtitle = `session-replay.failed-to-load-player-error`;
  } else {
    title = t(`session-replay.no-replay-found`);
    subtitle = `session-replay.no-replay-found-error`;
  }

  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateTitle}>{title}</div>
      <div className={styles.emptyStateSubtitle}>
        <Trans
          t={t}
          i18nKey={subtitle}
          components={{
            docsLink: (
              <a href={`https://docs.mixpanel.com/docs/session-replay/session-replay-web#faq`} target={`_blank`} />
            ),
          }}
        />
      </div>
    </div>
  );
}

async function fetchSignedReplayParams(replay: RecordingMetadata): Promise<SignedReplayParams> {
  const url = appApiUrl(`replays/sign`, {projectId: getProjectId()});
  return apiClient.post<SignedReplayParams>(url, {replay_id: replay.replayId, replay_env: replay.replayEnv});
}

const PLAYER_URL = REPLAY_FROM_SAME_ORIGIN ? `/replay-embed` : window[`__MP_REPLAY_EMBED_URL`];
// How long before the event time to jump to when the user clicks on an event in seconds
export const EVENT_TIME_JUMP_OFFSET = 2;

const RECORDING_SPEED_STORAGE_KEY = `recordingPlayerSpeed`;

export function RecordingPlayer(props: RecordingPlayerProps) {
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [loadingRecording, setLoadingRecording] = useState<boolean>(true);
  const [errorState, setErrorState] = useState<ErrorStateType | null>(null);

  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordingFinished, setRecordingFinished] = useState<boolean>(false);
  const [recordingSpeed, setRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [lastUserRecordingSpeed, setLastUserRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [isCurrentPeriodInactive, setIsCurrentPeriodInactive] = useState<boolean>(false);
  const [replayInactivePeriods, setReplayInactivePeriods] = useState<RecordingInactivePeriod[]>([]);
  const [embedReady, setEmbedReady] = useState(false);
  const [currentURL, setCurrentURL] = useState<string>(``);

  const embedIframeRef = useRef<HTMLIFrameElement>(null);

  const currentProject = useCurrentProject();
  const {t} = useTranslation();
  const themesResource = useThemes();
  const localStorage = useLocalStorageManager();

  useEffect(() => {
    if (props.replay?.replayId) {
      sendReplayParams(props.replay);
    }
  }, [props.replay?.replayId, embedReady]);

  useEffect(() => {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration) {
      goToEventTime();
    }
  }, [props.currentRecordingEventSelectedTime, embedIframeRef.current, recordingDuration]);

  useEffect(() => {
    if (props.isSkipInactivityEnabled && isCurrentPeriodInactive) {
      setIsCurrentPeriodInactive(false);
      updatePlayerSpeed(lastUserRecordingSpeed, true);
    }
  }, [props.isSkipInactivityEnabled]);

  useEffect(() => {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        setRecordingProgress(0);
        sendEmbedMessage({recordingPaused: false});
      } else {
        sendEmbedMessage({recordingPaused: props.currentRecordingPaused});
      }
    }
  }, [props.currentRecordingPaused]);

  useEffect(() => {
    setRecordingSpeed(getSavedReplaySpeed());
  }, []);

  useReplayWindowMessageListener<ParentAppReplayMessage>(WindowMessageRecipient.ParentApp, window, (message) => {
    const payload = message.payload;

    if (payload.embedReady) {
      setEmbedReady(true);
    }

    if (payload.embedBlocked) {
      setErrorState(ErrorStateType.EmbedBlocked);
    }

    if (payload.recordingProgress) {
      setRecordingProgress(payload.recordingProgress);
      props.updateCurrentReplayEvent(payload.recordingProgress / 1000 + props.replay.time);
      updateSkippingInactiveStatus();
      findCurrentURL(payload.recordingProgress);
    }

    if (payload.recordingDuration) {
      setRecordingDuration(payload.recordingDuration);
    }

    if (`loadingRecording` in payload) {
      setLoadingRecording(payload.loadingRecording);
    }

    if (payload.recordingStarted) {
      setRecordingFinished(false);
      props.onToggleCurrentRecordingPaused(false);
      setLoadingRecording(false);
    }

    if (payload.recordingFinished) {
      setRecordingFinished(true);
      props.onFinishReplay(props.replay.replayId);
    }

    if (payload.recordingInactivePeriods) {
      setReplayInactivePeriods(payload.recordingInactivePeriods);
    }

    if (payload.error) {
      reporter.error(payload.error);
      setErrorState(ErrorStateType.NoReplayFound);
    }

    if (payload.playerClicked) {
      toggleRecording();
    }
  });

  async function sendReplayParams(replay: RecordingMetadata) {
    setLoadingRecording(true);
    const signedReplayParams = await fetchSignedReplayParams(replay);
    const loadReplayParams = {
      signedReplayParams,
      targetOrigin: window.location.origin,
    };
    sendEmbedMessage({loadReplayParams});
    setRecordingFinished(false);
    props.onStartReplay(replay.replayId);
    setLoadingRecording(false);
  }

  function updateSkippingInactiveStatus() {
    const currentTime = props.replay.time * 1000 + recordingProgress;
    const nextInactivePeriod = replayInactivePeriods.find((period) => {
      return currentTime >= period.startTime && currentTime <= period.endTime;
    });

    if (props.isSkipInactivityEnabled && nextInactivePeriod) {
      const skipToTime = nextInactivePeriod.endTime - props.replay.time * 1000;
      sendEmbedMessage({recordingProgress: skipToTime});
      setIsCurrentPeriodInactive(false);
    } else if (Boolean(nextInactivePeriod) !== isCurrentPeriodInactive) {
      setIsCurrentPeriodInactive(Boolean(nextInactivePeriod));
      if (nextInactivePeriod) {
        updatePlayerSpeed(RecordingSpeed.Dectuple, true);
      } else {
        updatePlayerSpeed(lastUserRecordingSpeed, true);
      }
    }
  }

  function findCurrentURL(progress: number) {
    let newCurrentURL = props.replay?.startingURL || t(`session-replay.unknown-starting-url`);

    if (props.replay && props.replayPageViewEvents) {
      [...props.replayPageViewEvents]
        .sort((a, b) => a.eventTime - b.eventTime)
        .forEach((event) => {
          if (event.eventTime <= props.replay.time + progress / 1000) {
            newCurrentURL = event.currentURL;
          }
        });
    }

    setCurrentURL(newCurrentURL);
  }

  function getSavedReplaySpeed(): RecordingSpeed {
    const savedSpeed = localStorage.getItem(RECORDING_SPEED_STORAGE_KEY);
    if (savedSpeed) {
      return Number(savedSpeed) as RecordingSpeed;
    }
    return RecordingSpeed.Normal;
  }

  function saveReplaySpeed(speed: RecordingSpeed) {
    localStorage.setItem(RECORDING_SPEED_STORAGE_KEY, speed.toString());
  }

  function sendEmbedMessage(payload: EmbedReplayPayload) {
    if (embedIframeRef.current) {
      sendEmbedReplayMessage(
        {...payload, isDarkMode: themesResource.isDarkMode()},
        embedIframeRef.current.contentWindow,
      );
    }
  }

  function goToEventTime() {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration) {
      const targetEventTime = props.currentRecordingEventSelectedTime - EVENT_TIME_JUMP_OFFSET;
      const timeFromRecordingStart = Math.max(targetEventTime - props.replay.time, 0);
      sendEmbedMessage({
        recordingProgress: timeFromRecordingStart * 1000,
        recordingPaused: props.currentRecordingPaused,
      });
      if (timeFromRecordingStart * 1000 < recordingDuration) {
        setRecordingFinished(false);
      }
      findCurrentURL(timeFromRecordingStart * 1000);
    }
  }

  function toggleRecording() {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        props.onToggleCurrentRecordingPaused(false);
        setRecordingProgress(0);
        sendEmbedMessage({recordingPaused: false});
      } else {
        props.onToggleCurrentRecordingPaused(!props.currentRecordingPaused);
        sendEmbedMessage({recordingPaused: !props.currentRecordingPaused});
      }
    }
  }

  function updatePlayerSpeed(newSpeed: RecordingSpeed, inactivityChange = false) {
    if (embedIframeRef.current) {
      setRecordingSpeed(newSpeed);
      sendEmbedMessage({recordingSpeed: newSpeed});
      if (!inactivityChange) {
        setLastUserRecordingSpeed(newSpeed);
        saveReplaySpeed(newSpeed);
      }
    }
  }

  function updatePlayerProgress(event) {
    if (embedIframeRef.current && progressBarRef.current && !loadingRecording) {
      setLoadingRecording(true);
      const boundingBox = progressBarRef.current.getBoundingClientRect();
      const xDelta = event.clientX - boundingBox.left;
      const percentage = xDelta / boundingBox.width;
      const timeOffset = percentage * recordingDuration;
      setRecordingProgress(timeOffset);
      sendEmbedMessage({recordingProgress: timeOffset});
    }
  }

  const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar(recordingProgress, recordingDuration);
  const currentURLValue = currentURL || t(`session-replay.unknown-starting-url`);

  return (
    <div className={styles.recordingContent}>
      {props.replay?.source === 'web' && (
        <div className={styles.recordingUrlDisplay}>
          <CopyToClipboardButtonReact
            className={styles.recordingUrlDisplayIcon}
            copyType={CopyType.Link}
            options={[
              {
                value: currentURLValue,
              },
            ]}
            theme={ButtonTheme.Tertiary}
            disabled={!props.replayPageViewEventsLoaded}
          />
          {!props.replayPageViewEventsLoaded || !props.replay ? (
            <SkeletonListReact
              className={styles.recordingUrlDisplaySkeleton}
              numRows={1}
              rowSize={SkeletonListRowSize.Large}
            />
          ) : (
            <div className={styles.recordingUrlDisplayText}>
              {currentURLValue}
              <TooltipReact text={currentURLValue} />
            </div>
          )}
          {!props.hasReplayPageViewEvents && <TooltipReact text={t(`session-replay.no-page-view-events`)} />}
        </div>
      )}
      <div
        className={styles.playerIframeContainer}
        role={`region`}
        style={loadingRecording || errorState ? {visibility: `hidden`} : {}}
        onError={(error) => {
          if (currentProject.get().value?.id === 3 || currentProject.get().value?.id === 132990) {
            error.preventDefault();
          }
        }}
        onClick={() => toggleRecording()}
      >
        {/* interactions with the actual player must be tested separately */}
        {!isTestEnv() && <iframe className={styles.playerIframe} src={PLAYER_URL} ref={embedIframeRef} />}
      </div>
      <div className={styles.playerControls}>
        {embedIframeRef.current && (
          <div className={styles.playerControlsSpace}>
            <div className={styles.playerControlsProgressBarArea} ref={progressBarRef}>
              <RecordingPlayerTimeline
                replay={props.replay}
                replayEvents={props.replayEvents}
                recordingDuration={recordingDuration}
                recordingProgress={recordingProgress}
                recordingSkippingInactive={isCurrentPeriodInactive}
                replayInactivePeriods={replayInactivePeriods}
                searchFilter={props.searchFilter}
                updatePlayerProgress={updatePlayerProgress}
              />
            </div>
            <div className={styles.playerControlsArea}>
              <div className={styles.playerControlsAreaLeft}>
                <ButtonReact
                  className={styles.primaryButton}
                  icon={recordingFinished ? `restart` : props.currentRecordingPaused ? `play` : `pause`}
                  onClick={() => toggleRecording()}
                  theme={ButtonTheme.Primary}
                />
                <SelectReact
                  className={styles.playerControlsAreaSelect}
                  sections={[
                    {
                      items: [
                        {value: RecordingSpeed.Half, label: `0.5x`},
                        {value: RecordingSpeed.ThreeQuarters, label: `0.75x`},
                        {value: RecordingSpeed.Normal, label: `1x`},
                        {value: RecordingSpeed.Double, label: `2x`},
                        {value: RecordingSpeed.Quadruple, label: `4x`},
                        {value: RecordingSpeed.Octuple, label: `8x`},
                        {value: RecordingSpeed.Dectuple, label: `10x`},
                      ],
                    },
                  ]}
                  selectedOption={`${recordingSpeed}x`}
                  onChange={(event) => updatePlayerSpeed(event.detail.item.value)}
                  menuAlignment={Directions.Up}
                />
                {props.showSkipInactiveSwitch && (
                  <div className={styles.skipInactivity}>
                    <SwitchReact
                      on={props.isSkipInactivityEnabled}
                      onChange={(ev) => {
                        props.onToggleSkipInactivity(ev.detail.on);
                      }}
                    />
                    <span>Skip Inactivity</span>
                  </div>
                )}
              </div>
              <div>{`${formatRecordingProgressTimeBarResult.formattedProgress} / ${formatRecordingProgressTimeBarResult.formattedDuration}`}</div>
              <div className={styles.playerControlsAreaRight}>
                <ButtonReact
                  className={styles.expandButton}
                  icon={props.isFullscreen ? `collapse-diagonal` : `expand`}
                  onClick={() => props.onToggleFullscreen()}
                  tooltip={{text: t(`session-replay.toggle-fullscreen`)}}
                />
                {props.copyButton}
              </div>
            </div>
          </div>
        )}
      </div>
      {props.replay && (
        <>
          {errorState ? (
            <ErrorState type={errorState} />
          ) : loadingRecording ? (
            <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Large} role={`progressbar`} />
          ) : null}
        </>
      )}
      {props.children}
    </div>
  );
}
